import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Linkedin, Facebook, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const Footer = ({ onOpenAudit }) => {
  const { t, lang } = useLang();

  return (
    <footer data-testid="site-footer" className="bg-brand-bg relative overflow-hidden">
      <div className="border-t border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-full border border-brand-purple grid place-items-center text-brand-purpleLight font-heading font-black text-sm">
                P
              </div>
              <span className="font-heading font-black tracking-tighter text-base text-white">
                PRIME<span className="text-white">/</span>COLLECTIVE
              </span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-xs mb-5">{t.footer.tag}</p>
            <button
              data-testid="footer-audit-button"
              onClick={onOpenAudit}
              className="btn-holo inline-flex items-center gap-2 text-white font-bold uppercase tracking-wide px-5 py-3 rounded-full text-[11px]"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>

          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-purpleLight mb-4">
              {t.footer.navTitle}
            </h4>
            <ul className="space-y-2">
              {[
                { to: "/", label: t.nav.home },
                { to: "/services", label: t.nav.services },
                { to: "/portfolio", label: t.nav.portfolio },
                { to: "/about", label: t.nav.about },
                { to: "/blog", label: t.nav.blog },
                { to: "/contact", label: t.nav.contact },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-zinc-300 hover:text-brand-purpleLight transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-purpleLight mb-4">
              {t.footer.servicesTitle}
            </h4>
            <ul className="space-y-2">
              {t.services.list.map((s) => (
                <li key={s.num}>
                  <Link to="/services" className="text-sm text-zinc-300 hover:text-brand-purpleLight transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-purpleLight mb-4">
              {t.footer.contactTitle}
            </h4>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li><a href="mailto:contact@primecollective.bg" className="hover:text-brand-purpleLight transition-colors">contact@primecollective.bg</a></li>
              <li><a href="tel:+359881234567" className="hover:text-brand-purpleLight transition-colors">+359 885 333 750</a></li>
              <li>{t.contact.info.location}</li>
            </ul>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-purpleLight mb-4 mt-8">
              {t.footer.socialTitle}
            </h4>
            <div className="flex items-center gap-3">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/primecollective.bg/", label: "Instagram" },
                { Icon: Linkedin, href: "https://linkedin.com/", label: "LinkedIn" },
                { Icon: Facebook, href: "https://facebook.com/", label: "Facebook" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label} href={href} target="_blank" rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 grid place-items-center rounded-full border border-brand-border hover:border-brand-purple hover:text-white hover:bg-brand-purple/10 transition-all text-zinc-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden border-t border-brand-border">
        <svg
          viewBox="0 0 1600 130"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          className="block w-full h-auto py-3"
        >
          <text
            x="800"
            y="100"
            textAnchor="middle"
            className="shimmer-text"
            style={{
              fontFamily: "Unbounded, sans-serif",
              fontWeight: 900,
              fontSize: "120px",
              letterSpacing: "-4px",
              textTransform: "uppercase",
              fill: "url(#footerShimmer)",
            }}
          >
            PRIME COLLECTIVE
          </text>
          <defs>
            <linearGradient id="footerShimmer" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="50%" stopColor="#C084FC" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="border-t border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>{t.footer.copyright}</p>
          <div className="flex items-center gap-6 font-mono uppercase tracking-[0.2em]">
            <a href="#" className="hover:text-brand-purpleLight transition-colors">{t.footer.privacy}</a>
            <a href="#" className="hover:text-brand-purpleLight transition-colors">{t.footer.terms}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
